/* import __COLOCATED_TEMPLATE__ from './test-result.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import type Action from 'embercom/models/workflow-connector/action';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type Store from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency-decorators';
import { post } from 'embercom/lib/ajax';
import { isBlank } from '@ember/utils';
import { type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import { PARAM_DESCRIPTOR_SOURCE_CUSTOM } from 'embercom/models/workflow-connector/constants/parameter-descriptor-sources';

interface Args {
  workflowAction: Action;
  templatedParameters: any;
  clearTemplatedParameter: () => void;
  saveTemplatedParameter: (requestParameter: any, requestValue: any) => void;
  nextSection: (sectionId: string) => void;
}

export default class TestResult extends Component<Args> {
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service intercomEventService: any;
  @service declare store: Store;

  @tracked declare bannerText: string;
  @tracked declare bannerType: 'confirmation' | 'error' | 'warning';
  @tracked declare testResponse: any;
  @tracked declare responseBodyToDisplay: any;
  @tracked disableTestButton = false;
  @tracked attributeNames: any = {};
  @tracked showXmlConversionMessage = false;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.getAttributeNames();
    this.requestParameters();
    this.getValidTest();
    this.setDisableTestButton();
  }

  getAttributeNames() {
    Object.entries(this.args.workflowAction.attributeData).forEach(([identifier, data]) => {
      this.attributeNames[identifier] = data.name;
    });
    return;
  }

  requestParameters() {
    let currentParameters = this.args.templatedParameters;
    this.args.clearTemplatedParameter();

    if (this.args.workflowAction.mcpServerId) {
      this.args.workflowAction.enabledParamDescriptorNames.forEach((descriptorName) => {
        let paramDescriptor = this.args.workflowAction.paramDescriptors.find(
          (pd) => pd.name === descriptorName,
        );

        let value = '';
        if (paramDescriptor?.source === PARAM_DESCRIPTOR_SOURCE_CUSTOM && paramDescriptor.value) {
          value = String(paramDescriptor.value);
        }
        this.args.saveTemplatedParameter(descriptorName, value);
      });
    } else {
      Array.from(this.args.workflowAction.attributes).map((attribute) =>
        this.args.saveTemplatedParameter(attribute[1], currentParameters[attribute[1]] || ''),
      );
    }
  }

  getValidTest() {
    if (this.args.workflowAction.testIsValid) {
      let body = this.args.workflowAction.testResult.actionResponse.body;
      let status = this.args.workflowAction.testResult.actionResponse.status.toString();
      let code = this.args.workflowAction.testResult.actionResponse.returnCode;
      let success = this.args.workflowAction.testResult.actionResponse.success;

      this.responseBodyToDisplay = JSON.stringify(body, null, 2);
      this._setBannerSuccessMessage(status, code, success);
    } else if (this.args.workflowAction.isJsonInvalid) {
      this._setInvalidJsonMessage();
    }
  }

  setDisableTestButton() {
    this.disableTestButton = Object.values(this.args.templatedParameters).some(isBlank);
  }

  private isXmlFormat(content: string): boolean {
    if (!content || typeof content !== 'string') {
      return false;
    }

    let trimmed = content.trim();
    return (
      trimmed.startsWith('<?xml') ||
      (trimmed.startsWith('<') && trimmed.endsWith('>') && trimmed.includes('</'))
    );
  }

  validateRequestBody() {
    if (
      ['get', 'delete'].includes(this.args.workflowAction.httpMethod) &&
      !this.args.workflowAction.mcpServerId
    ) {
      this.args.workflowAction.body = null;
    }
  }

  get showTestValuesHeaders() {
    return Object.keys(this.args.templatedParameters).length;
  }

  @action
  nextSection() {
    this.args.nextSection('response_section');
  }

  @action
  setParameterValue(requestParameter: any, event: any) {
    this.args.saveTemplatedParameter(requestParameter, event.target.value);
    this.setDisableTestButton();
  }

  @task({ drop: true })
  *testRequest(): TaskGenerator<void> {
    this.validateRequestBody();

    let isNewAction = !this.args.workflowAction.id;
    if (isNewAction) {
      try {
        yield this.args.workflowAction.save();
      } catch (error) {
        this._setBannerOnException({
          errorThrown: this.intl.t('workflow-connector.builder.body.testing.save-error'),
        });
        return;
      }
    }

    let params = {
      app_id: this.appService.app.id,
      workflow_action: this.args.workflowAction,
      templated_values: {
        ...this.args.templatedParameters,
      },
    };

    try {
      let testResponse = yield post(
        `/ember/workflow_connector/actions/${this.args.workflowAction.id}/test`,
        params,
      );
      this.testResponse = testResponse.test_result;
      this.responseBodyToDisplay = this.getResponseBody(this.testResponse.action_response);

      let normalizedTestResult = (
        this.store.normalize('workflow-connector/test-result', this.testResponse) as any
      ).data.attributes;
      let testResult = this.store.createFragment(
        'workflow-connector/test-result',
        normalizedTestResult,
      );
      this.args.workflowAction.testResult = testResult;

      let responseFields = testResponse.response_fields.map((responseField: any) => {
        let normalizedResponseFields = (
          this.store.normalize('workflow-connector/response-field', responseField) as any
        ).data.attributes;

        return this.store.createFragment(
          'workflow-connector/response-field',
          normalizedResponseFields,
        );
      });

      this.args.workflowAction.responseFields = responseFields;
      this._setBannerParams();
    } catch (exception) {
      this._setBannerOnException(exception);
    }

    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'test_request',
      action_id: this.args.workflowAction.id,
    });
  }

  get isTestResultRunning() {
    return taskFor(this.testRequest).isRunning;
  }

  _setBannerParams() {
    if (this.testResponse.action_response.success) {
      if (this.args.workflowAction.isJsonInvalid) {
        this._setInvalidJsonMessage();
        return;
      }

      this._setBannerSuccessMessage(
        this.testResponse.action_response.status.toString(),
        this.testResponse.action_response.return_code,
        this.testResponse.action_response.success,
      );
      this.args.workflowAction.setTestedRequest();
    } else {
      this.bannerText = this._getErrorMessage(this.testResponse.action_response);
      this.bannerType = 'error';
    }
  }

  _setBannerSuccessMessage(status: string, code: string, success: boolean) {
    let isSuccessHttpStatus = status.startsWith('2');
    if (code && code.toLowerCase() === 'ok') {
      code = code.toUpperCase();
    }

    let statusText = code ? `${status} ${code}` : status;

    if (success && isSuccessHttpStatus && this.args.workflowAction.mcpServerId) {
      let errorMessage = this.extractErrorMessage();

      if (errorMessage) {
        this.bannerText = errorMessage;
        this.bannerType = 'warning';
        return;
      }
    }

    if (success && isSuccessHttpStatus && this.showXmlConversionMessage) {
      this.bannerText = this.intl.t(
        'workflow-connector.builder.body.testing.success-response-with-xml-conversion',
        {
          status: statusText,
          htmlSafe: true,
        },
      );
    } else if (success && isSuccessHttpStatus) {
      this.bannerText = this.intl.t('workflow-connector.builder.body.testing.success-response', {
        status: statusText,
        htmlSafe: true,
      });
    } else {
      this.bannerText = statusText;
    }

    this.bannerType = 'confirmation';
  }

  _setInvalidJsonMessage() {
    this.bannerText = this.intl.t('workflow-connector.builder.body.testing.invalid-json');
    this.bannerType = 'error';
    this.responseBodyToDisplay = this.args.workflowAction.testResult?.actionResponse?.rawBody;
  }

  _getErrorMessage(action_response: any) {
    return (
      action_response.human_readable_error ||
      action_response.body?.errors ||
      this.intl.t('workflow-connector.builder.body.testing.error-message')
    );
  }

  _setBannerOnException(exception: any) {
    this.bannerType = 'error';
    this.bannerText = exception.errorThrown;
  }

  getResponseBody(action_response: any) {
    if (action_response.raw_body && action_response.body !== null) {
      let isRawBodyXml = this.isXmlFormat(action_response.raw_body);
      this.showXmlConversionMessage = isRawBodyXml;
    } else {
      this.showXmlConversionMessage = false;
    }

    return action_response.body !== null
      ? JSON.stringify(this.testResponse.action_response.body, null, 2)
      : action_response.raw_body;
  }

  private extractErrorMessage(): string | null {
    let response =
      this.testResponse?.action_response || this.args.workflowAction.testResult?.actionResponse;

    if (!response?.body || !response.body.result?.isError) {
      return null;
    }

    let firstText = response.body.result.content?.[0]?.text;
    if (typeof firstText === 'string') {
      return firstText.length > 100 ? `${firstText.substring(0, 100)}...` : firstText;
    }
    return this.intl.t('workflow-connector.builder.body.testing.mcp-error-without-details');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'WorkflowConnector::Builder::Body::Sections::Testing::TestResult': typeof TestResult;
    'workflow-connector/builder/body/sections/testing/test-result': typeof TestResult;
  }
}
